import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import Skeleton from "@mui/material/Skeleton";
import { styled } from "@mui/material/styles";
import NodeDataStore from "csi/datastore/NodeDataStore";
import DojoView from "csi/page/DojoView";
import { useStore } from "csi/state/hooks";
import { routerStore } from "csi/state/rootStore";
import WorkingCache from "csi/state/WorkingCache";
import { C8Node } from "csi/typings";
import xml from "csi/xml";
import Query from "csi/xml/Query";
import Search from "csi/xml/Search";
import { designType } from "designSystem/components/dataGrid/DataGrid";
import FlexColumn from "designSystem/components/flex/FlexColumn";
import ArrowCollapseHorizontal from "designSystem/components/icon/ArrowCollapseHorizontal";
import IconArrowExpandHorizontal from "designSystem/components/icon/ArrowExpandHorizontal";
import PaneHeader from "designSystem/components/pane/PaneHeader";
import { useLocalStore } from "designSystem/hooks";
import {
	Box,
	DataGrid,
	GalleryPlaceholder,
	Grid,
	IconButton,
} from "designSystem/index";
import { grey } from "designSystem/styles/color";
import { STANDARD_BORDER_STYLE } from "designSystem/styles/style-functions";
import { action, makeObservable, observable } from "mobx";
import { observer } from "mobx-react-lite";
import React, { createContext, FunctionComponent, useContext } from "react";
import { ComplianceAnalysisCardProps } from "./ComplianceAnalysisCard";
import ComplianceAnalysisMultiFilter, { FilterParams } from "./ComplianceAnalysisMultiFilter";
import { ComplianceAnalysisPaneComponent } from "./ComplianceAnalysisPaneComponent";
import getFilterData from "./ComplianceAnalysisSlimFilter";

const PREFIX = "ComplianceAnalysisSlimOnInstance";

const classes = {
	placeholder: `${PREFIX}placeholder`,
	filterSeparator: `${PREFIX}filterSeparator`,
	dojoView: `${PREFIX}dojoView`,
};

const StyledGrid = styled(FlexColumn)((
	{
		theme: Theme,
	},
) => ({
	[`& .${classes.placeholder}`]: {
		"& img": {
			width: 270,
		},
	},

	[`& .${classes.filterSeparator}`]: {
		borderBottomWidth: 1,
		borderBottomStyle: "solid",
		borderBottomColor: grey[400],
		height: 6,
	},

	[`& .${classes.dojoView}`]: {
		marginTop: "1em",
	},
}));

const TYPE_COMPLIANCE_ANALYSIS = "ProductComplianceAnalysis";
const TYPE_COMPLIANCE_ANALYSIS_TYPE = "ProductComplianceAnalysisType";

class ComplianceAnalysisNodeStore {
	@observable.ref private store: NodeDataStore;
	@observable.ref private typeStore: NodeDataStore;
	private filterData: FilterParams;
	private query: Query;
	private queryAttr: string;
	private queryContext: QueryContext;
	private queryDependencyPaths: string[];
	private queryPath: string;
	private cache: WorkingCache;
	private search: Search;

	constructor(queryAttr: string, queryContext: QueryContext, queryPath: string, queryDependencyPaths?: string[]) {
		makeObservable(this);
		// create filter data
		const { locationType, locationUrl } = routerStore;
		this.filterData = getFilterData(locationType, locationUrl);
		this.queryAttr = queryAttr;
		this.queryContext = queryContext;
		this.queryDependencyPaths = queryDependencyPaths;
		this.queryPath = queryPath;
		this.cache = new WorkingCache();

		this.populateTypes();
		this.updateFilterData();
	}

	/** handler to clean up - not used yet but must present*/
	onCleanup?: (instance: ComplianceAnalysisNodeStore) => void;

	@action public updateFilterData = () => {
		// create node store
		const { locationUrl, locationRevisionUrl } = routerStore;
		const queryContext = this.queryContext == QueryContext.LOCATION_NODE ? locationUrl : locationRevisionUrl;

		const search = new Search().byType(TYPE_COMPLIANCE_ANALYSIS);

		this.query = search.getQuery().SortByNode(xml.PROP_CREATE_TIME, null, true);
		this.query.RefAttr(this.queryAttr, xml.OP_EQUALS, queryContext, this.queryPath);

		if (this.queryDependencyPaths) {
			search.addDependencyPaths(this.queryDependencyPaths);
		}

		this.filterData.filterData.forEach((item) => {
			item.applyQueryFilter?.(this.query);
		});

		this.store = new NodeDataStore(search, this.cache);
	};

	@action public refreshStore = () => {
		this.updateFilterData();
	};

	@action public populateTypes = () => {
		// create node store
		const search = new Search().byType(TYPE_COMPLIANCE_ANALYSIS_TYPE);
		this.typeStore = new NodeDataStore(search, this.cache);
		this.typeStore.load();
	};

	get dataStore(): NodeDataStore {
		return this.store;
	}

	get typeDataStore(): NodeDataStore {
		return this.typeStore;
	}

	get multiFilterData() {
		return this.filterData;
	}
}

const ComplianceAnalysisStoreContext = createContext<ComplianceAnalysisNodeStore>(
	undefined,
);
const useComplianceAnalysisNodeStore = () => useContext(ComplianceAnalysisStoreContext);

export const ComplianceAnalysisSlimOnInstance = (
	ComplianceAnalysisCard: React.FunctionComponent<ComplianceAnalysisCardProps>,
	queryAttr: string,
	queryContext: QueryContext,
	queryPath: string,
	viewContext: DojoViewContext,
	viewId: string,
	viewNodeType: string,
	slimPanelDesign: designType,
	showInfoPanelOnExpanded?: boolean,
	hideViewTitle: boolean = false,
	queryDependencyPaths?: string[],
) => {
	const nodeStore = useLocalStore(() => new ComplianceAnalysisNodeStore(queryAttr, queryContext, queryPath, queryDependencyPaths));
	return (
		<>
			{nodeStore && (
				<ComplianceAnalysisStoreContext.Provider value={nodeStore}>
					<PrivateComplianceAnalysisOnSlimInstance
						hideViewTitle={hideViewTitle}
						showInfoPanelOnExpanded={showInfoPanelOnExpanded != null ? showInfoPanelOnExpanded : true}
						slimCard={ComplianceAnalysisCard}
						slimPanelDesign={slimPanelDesign}
						viewContext={viewContext}
						viewId={viewId}
						viewNodeType={viewNodeType}
					/>
				</ComplianceAnalysisStoreContext.Provider>
			)}
		</>
	);
};

export const enum DojoViewContext {
	SELECTED_NODE,
	LOCATION_NODE,
}

export const enum QueryContext {
	LOCATION_NODE,
	LOCATION_REVISION_NODE,
}

const getViewNodeUrl = (viewContext: DojoViewContext, selectedNode?: C8Node): string => {
	switch (viewContext) {
		case DojoViewContext.SELECTED_NODE:
			return selectedNode ? selectedNode.$URL : null;

		case DojoViewContext.LOCATION_NODE:
			return routerStore.locationUrl;

		default:
			return null;
	}
};

interface PrivateComplianceAnalysisOnSlimInstanceProps {
	slimCard: FunctionComponent<ComplianceAnalysisCardProps>;
	slimPanelDesign: designType;
	showInfoPanelOnExpanded: boolean;
	viewContext: DojoViewContext;
	viewId: string;
	viewNodeType: string;
	hideViewTitle?: boolean;
}

const PrivateComplianceAnalysisOnSlimInstance: FunctionComponent<PrivateComplianceAnalysisOnSlimInstanceProps> = observer((props) => {
	const { slimCard, slimPanelDesign, showInfoPanelOnExpanded, viewContext, viewId, viewNodeType, hideViewTitle } = props;
	const { rightPaneStore } = useStore();
	const selected = rightPaneStore.selected;
	const complianceSelected = selected as ComplianceAnalysisPaneComponent;
	const { complianceAnalysis, paneIsExpanded, config, setProductComplianceAnalysisNode } = complianceSelected;
	const viewNodeUrl = getViewNodeUrl(viewContext, complianceAnalysis);
	const viewTitle = complianceAnalysis?.$Name || "Unnamed";
	const displayDojoView = complianceAnalysis || viewContext == DojoViewContext.LOCATION_NODE;

	const SkeletonCard = () => (
		<Box
			bgcolor="background.paper"
			border={STANDARD_BORDER_STYLE}
			height={1}
			p={2}
		>
			<Skeleton height={100} variant="rectangular" />
		</Box>
	);

	const PlaceholderComponent = () => (
		<GalleryPlaceholder
			className={classes.placeholder}
			titleProps={{ res: "ComplianceAnalysisNoResults" }}
		/>
	);

	const ExpandedPlaceholderComponent = () => (
		<GalleryPlaceholder
			className={classes.placeholder}
			titleProps={{ res: "ComplianceAnalysisNoResultsExpanded" }}
		/>
	);

	// for Closing right panel from X
	const onClose = () => {
		complianceSelected.setPaneIsExpanded(false);
		rightPaneStore.closeSelected();
	};

	// For config onLeave function
	const onCloseSelected = () => {
		complianceSelected.setPaneIsExpanded(false);
		rightPaneStore.collapseSelected();
	};

	const onOpenDetailedView = () => {
		complianceSelected.setPaneIsExpanded(true);
		rightPaneStore.expandSelected();
	};

	const onCloseDetailedView = () => {
		complianceSelected.setPaneIsExpanded(false);
		rightPaneStore.collapseSelected();
	};

	const {
		dataStore,
		typeDataStore,
		multiFilterData,
		updateFilterData,
		refreshStore,
	} = useComplianceAnalysisNodeStore();

	const onRefresh = () => {
		refreshStore();
	};

	const onFilterChange = () => {
		updateFilterData();
	};

	const deleteNode = (id: string) => {
		dataStore.remove(id);
	};

	config.onLeave = onCloseSelected;

	return (
		<StyledGrid>
			<PaneHeader titleRes="ProductComplianceAnalysis">
				{!paneIsExpanded && (
					<>
						<IconButton
							design="default"
							onClick={onRefresh}
							res="Refresh"
							size="large"
						>
							<RefreshIcon />
						</IconButton>
						<IconButton
							design="default"
							onClick={onOpenDetailedView}
							res="ComplianceAnalysisExpanded"
							size="large"
						>
							<IconArrowExpandHorizontal />
						</IconButton>
					</>
				)}
				{paneIsExpanded && (
					<>
						<IconButton
							design="default"
							onClick={onRefresh}
							res="Refresh"
							size="large"
						>
							<RefreshIcon />
						</IconButton>
						<IconButton
							design="default"
							onClick={onCloseDetailedView}
							res="ComplianceAnalysisCollapse"
							size="large"
						>
							<ArrowCollapseHorizontal />
						</IconButton>
					</>
				)}
				<IconButton
					design="default"
					onClick={onClose}
					res="BreadcrumbComplianceClose"
					size="large"
				>
					<CloseIcon />
				</IconButton>
			</PaneHeader>
			<Grid
				item
				style={{ display: "flex", minHeight: "0" }}
				xs={12}
			>
				{(!paneIsExpanded || showInfoPanelOnExpanded) && (
					<Box
						overflow="scroll"
						sx={{
							width: paneIsExpanded ? "20%" : "100%",
							minWidth: paneIsExpanded ? "20%" : "100%",
							flexShrink: 0,
							flexGrow: 0,
							overflowX: "hidden",
							overflowY: "auto",
						}}
					>
						<ComplianceAnalysisMultiFilter filterParams={multiFilterData} onChange={onFilterChange} />
						<Box className={classes.filterSeparator} />
						<DataGrid
							CardComponent={slimCard}
							PlaceholderComponent={PlaceholderComponent}
							SkeletonComponent={SkeletonCard}
							additionalProps={{ typeStore: typeDataStore }}
							autoSelect
							columns={1}
							design={slimPanelDesign}
							infiniteScrolling
							onClickItem={setProductComplianceAnalysisNode}
							onDeleteItem={deleteNode}
							rowsPerPage={50}
							showIndex
							store={dataStore}
						/>
					</Box>
				)}
				{paneIsExpanded && displayDojoView && (
					<Box
						display="flex"
						flexDirection="column"
						flexGrow={1}
						minHeight={0}
						padding="1rem"
						width="65%"
					>
						{!!viewTitle && !hideViewTitle && (
							<PaneHeader title={viewTitle} />
						)}
						<DojoView
							className={classes.dojoView}
							fullHeight
							nodeUrl={viewNodeUrl}
							style={{ flexGrow: 1, minHeight: 0, overflow: "auto" }}
							viewId={viewId}
							viewNodeType={viewNodeType}
							viewParams={{ bFocused: true }}
						/>
					</Box>

				)}
				{paneIsExpanded && !displayDojoView && (
					<Box
						flexGrow={1}
						overflow="auto"
						sx={{ width: "100%" }}
					>
						<ExpandedPlaceholderComponent />
					</Box>
				)}
			</Grid>
		</StyledGrid>
	);
});

export default ComplianceAnalysisSlimOnInstance;
